import { Fragment, memo } from "react";

import { type FilterValue } from "@shopify/hydrogen-react/storefront-api-types";

import { CollectionContentCard } from "@components/CollectionContentCard";
import { ProductCard, ProductCardPlaceholder } from "@components/ProductCard";
import {
  type CollectionContentCard as CollectionContentCardType,
  type OPProductConnection,
} from "@root/tablescape/state";
import { tw } from "classnames";

type Props = {
  appliedColorFilters: FilterValue[];
  bundleValueText: string;
  contentCards: Array<{
    contentCard: CollectionContentCardType;
    id: string;
    position: string;
  }> | null;
  enableQuickAdd?: boolean;
  isLoadingMore: boolean;
  nextLabel: string;
  pageSize: number;
  pageType: string;
  prevLabel: string;
  productPositions: string[];
  productResults: Array<OPProductConnection>;
  quickAddButtonText?: string;
  showContentCards: boolean;
  variantMatchCollectionName?: string;
};

const styles = {
  base: tw(
    "m-0",
    "grid",
    "list-none",
    "grid-flow-row-dense",
    "grid-cols-2",
    "gap-x-4",
    "gap-y-10",
    "p-0",
    "md:grid-flow-row",
    "md:gap-x-6",
    "md:gap-y-12",
    "mg:grid-cols-3",
    "pb-15"
  ),
};

const CollectionGridViewBase = ({
  appliedColorFilters,
  bundleValueText,
  contentCards,
  enableQuickAdd,
  isLoadingMore,
  nextLabel,
  pageSize,
  pageType,
  prevLabel,
  productPositions,
  productResults,
  quickAddButtonText,
  showContentCards,
  variantMatchCollectionName,
}: Props) => {
  return (
    <ol className={styles.base}>
      {showContentCards
        ? productResults?.map((product, index) => {
            const contentCard = contentCards?.find(
              (card) => parseInt(card.position, 10) === index
            );

            return (
              <Fragment key={`productCard-${product.id}`}>
                <ProductCard
                  appliedColorFilters={appliedColorFilters}
                  bundleValueText={bundleValueText}
                  collectionPosition={productPositions[index]}
                  enableQuickAdd={enableQuickAdd}
                  nextLabel={nextLabel}
                  pageType={pageType}
                  prevLabel={prevLabel}
                  product={product}
                  quickAddButtonText={quickAddButtonText}
                  variantMatchCollectionName={variantMatchCollectionName}
                />
                {contentCard && (
                  <CollectionContentCard {...contentCard.contentCard} />
                )}
              </Fragment>
            );
          }) ?? []
        : productResults?.map((product, index) => (
            <ProductCard
              appliedColorFilters={appliedColorFilters}
              bundleValueText={bundleValueText}
              collectionPosition={productPositions[index]}
              enableQuickAdd={enableQuickAdd}
              key={`productCard_${product.id}`}
              nextLabel={nextLabel}
              pageType={pageType}
              prevLabel={prevLabel}
              product={product}
              quickAddButtonText={quickAddButtonText}
              variantMatchCollectionName={variantMatchCollectionName}
            />
          ))}
      {isLoadingMore &&
        Array.from({ length: pageSize }).map((_, index) => (
          <ProductCardPlaceholder key={`placeholder-productCard-${index}`} />
        ))}
    </ol>
  );
};

export const CollectionGridView = memo(CollectionGridViewBase);
